import * as XLSX from 'xlsx';
import { AccommodationTable, Room, Bed } from '../types/accommodation';

type ExportRow = {
  hotel_name: string;
  package_name?: string; // After hotel name
  country?: string; // After package name
  passport_number?: string; // Added passport number
  hotel_table_name: string; // We'll keep for processing but exclude from export
  room_number: string;
  room_type: string;
  bed_number: number;
  pilgrim_index: number; // Now based on non-conflicting periods
  check_in: string;
  check_out: string;
  pilgrim_name: string;
  pilgrim_gender: string;
  guide_name: string;
  organizer_name: string;
  pilgrim_age: number;
};

export const exportAccommodationToExcel = async (table: AccommodationTable) => {
  try {
    console.log('Starting accommodation export for table:', {
      hotelName: table.hn,
      tableName: table.tn,
      roomCount: table.r?.length
    });

    const rows: ExportRow[] = [];

    // Import Firebase functions
    const { getDocs, collection, query, where } = await import('firebase/firestore');
    const { firestore } = await import('../lib/firebase/config');
    
    // Fetch pilgrims to get package names and countries
    const pilgrimsRef = collection(firestore, 'Pilgrims');
    const pilgrimsSnapshot = await getDocs(pilgrimsRef);
    
    // Create a map of pilgrim IDs to their package names, countries and passport numbers
    const pilgrimDetails = new Map();
    pilgrimsSnapshot.forEach(doc => {
      const data = doc.data();
      pilgrimDetails.set(doc.id, {
        package: data.package_nickName || '',
        country: data.Residence_Country || '',
        passport: data.Passport_Number || ''
      });
    });

    // Convert table data to rows
    table.r?.forEach((room: Room) => {
      if (!room.b) {
        console.warn('Room has no beds:', room.n);
        return;
      }

      room.b.forEach((bed: Bed, bedIndex: number) => {
        // Get all assignments for the bed
        if (bed.a && bed.a.length > 0) {
          // Helper function to check if two date ranges overlap
          const checkOverlap = (start1: number | string, end1: number | string, start2: number | string, end2: number | string) => {
            // Convert to timestamps for comparison
            const start1Timestamp = typeof start1 === 'string' ? parseDate(start1) : start1;
            const end1Timestamp = typeof end1 === 'string' ? parseDate(end1) : end1;
            const start2Timestamp = typeof start2 === 'string' ? parseDate(start2) : start2;
            const end2Timestamp = typeof end2 === 'string' ? parseDate(end2) : end2;
            
            return start1Timestamp < end2Timestamp && start2Timestamp < end1Timestamp;
          };
          
          // Helper function to parse DD/MM/YYYY format to timestamp
          const parseDate = (dateStr: string): number => {
            if (!dateStr || typeof dateStr !== 'string' || !dateStr.includes('/')) {
              return 0;
            }
            try {
              const [day, month, year] = dateStr.split('/').map(Number);
              if (isNaN(day) || isNaN(month) || isNaN(year)) {
                return 0;
              }
              return new Date(year, month - 1, day).getTime();
            } catch (e) {
              console.error('Error parsing date:', dateStr, e);
              return 0;
            }
          };

          // Group assignments by non-overlapping periods
          const periods: { assignments: any[]; startDate: number | string; endDate: number | string }[] = [];
          
          // Sort assignments by check-in date
          const sortedAssignments = [...bed.a].sort((a, b) => {
            // Convert to timestamps for comparison
            const aTimestamp = typeof a.ci === 'string' ? parseDate(a.ci) : a.ci;
            const bTimestamp = typeof b.ci === 'string' ? parseDate(b.ci) : b.ci;
            return aTimestamp - bTimestamp;
          });
          
          sortedAssignments.forEach(assignment => {
            const assignmentStart = assignment.ci;
            const assignmentEnd = assignment.co;
            
            // Try to find an existing period that overlaps
            let foundPeriod = false;
            for (const period of periods) {
              if (checkOverlap(period.startDate, period.endDate, assignmentStart, assignmentEnd)) {
                // Overlapping period found
                period.assignments.push(assignment);
                // Update period bounds if needed
                if (typeof period.startDate === 'number' && typeof assignmentStart === 'number') {
                  period.startDate = Math.min(period.startDate, assignmentStart);
                }
                if (typeof period.endDate === 'number' && typeof assignmentEnd === 'number') {
                  period.endDate = Math.max(period.endDate, assignmentEnd);
                }
                foundPeriod = true;
                break;
              }
            }
            
            // If no overlapping period found, create a new one
            if (!foundPeriod) {
              periods.push({
                assignments: [assignment],
                startDate: assignmentStart,
                endDate: assignmentEnd
              });
            }
          });
          
          // Sort periods by start date
          periods.sort((a, b) => {
            const aTimestamp = typeof a.startDate === 'string' ? parseDate(a.startDate) : a.startDate;
            const bTimestamp = typeof b.startDate === 'string' ? parseDate(b.startDate) : b.startDate;
            return aTimestamp - bTimestamp;
          });
          
          // Process each period with its assignments
          periods.forEach((period, periodIndex) => {
            period.assignments.forEach((assignment, assignmentIndex) => {
              if (!assignment) {
                console.warn('Invalid assignment found in bed:', bedIndex);
                return;
              }

              try {
                // Get pilgrim details
                const details = pilgrimDetails.get(assignment.pid) || { package: '', country: '', passport: '' };

                // Helper function to format date string
                const formatDateString = (dateInput: number | string): string => {
                  // For string format in DD/MM/YYYY
                  if (typeof dateInput === 'string' && dateInput.includes('/')) {
                    // Extract day and month only
                    const [day, month] = dateInput.split('/').map(num => parseInt(num, 10));
                    // Return DD/M format
                    return `${day}/${month}`;
                  }
                  
                  // For timestamp
                  try {
                    const date = new Date(dateInput);
                    if (isNaN(date.getTime())) {
                      console.warn('Invalid date input:', dateInput);
                      return 'Invalid Date';
                    }
                    // Return in DD/M format without leading zeros for month
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  } catch (error) {
                    console.error('Error formatting date:', error, dateInput);
                    return 'Invalid Date';
                  }
                };

                rows.push({
                  hotel_name: table.hn || '',
                  package_name: details.package,
                  country: details.country,
                  passport_number: details.passport,
                  hotel_table_name: table.tn || '', // Will be excluded from export
                  room_number: String(room.n) || '',
                  room_type: getRoomTypeLabel(room.t),
                  bed_number: bedIndex + 1,
                  pilgrim_index: periodIndex + 1, // Using period index instead of assignment index
                  check_in: formatDateString(assignment.ci),
                  check_out: formatDateString(assignment.co),
                  pilgrim_name: assignment.pn || '',
                  pilgrim_gender: assignment.g === 'M' ? 'Male' : 'Female',
                  guide_name: assignment.gn || '',
                  organizer_name: assignment.on || '',
                  pilgrim_age: assignment.age || 0
                });
              } catch (error) {
                console.error('Error processing assignment:', error, {
                  assignment,
                  room: room.n,
                  bed: bedIndex
                });
              }
            });
          });
        }
      });
    });

    console.log('Generated export rows:', rows.length);

    if (rows.length === 0) {
      console.warn('No data to export');
      throw new Error('No data available to export');
    }

    // Define the columns to include in the export (excluding hotel_table_name)
    const columns = [
      'hotel_name',
      'package_name',
      'country',
      'passport_number',
      'room_number',
      'room_type',
      'bed_number',
      'pilgrim_index',
      'check_in',
      'check_out',
      'pilgrim_name',
      'pilgrim_gender',
      'guide_name',
      'organizer_name',
      'pilgrim_age'
    ];

    // Create worksheet with only selected columns
    const ws = XLSX.utils.json_to_sheet(
      rows.map(row => {
        const newRow: any = {};
        columns.forEach(col => {
          newRow[col] = row[col as keyof ExportRow];
        });
        return newRow;
      })
    );

    // Add English headers
    XLSX.utils.sheet_add_aoa(ws, [[
      'Hotel Name',
      'Package Name',
      'Country',
      'Passport Number',
      'Room Number',
      'Room Type',
      'Bed Number',
      'Period Index',
      'Check-in Date',
      'Check-out Date',
      'Pilgrim Name',
      'Gender',
      'Guide',
      'Organizer',
      'Age'
    ]], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // hotel_name
      { wch: 25 }, // package_name
      { wch: 20 }, // country
      { wch: 15 }, // passport_number
      { wch: 10 }, // room_number
      { wch: 15 }, // room_type
      { wch: 10 }, // bed_number
      { wch: 12 }, // pilgrim_index
      { wch: 15 }, // check_in
      { wch: 15 }, // check_out
      { wch: 30 }, // pilgrim_name
      { wch: 10 }, // pilgrim_gender
      { wch: 25 }, // guide_name
      { wch: 25 }, // organizer_name
      { wch: 10 }, // pilgrim_age
    ];

    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accommodation');

    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const filename = `${table.hn}-accommodation-${date}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);

    console.log('Export completed successfully');
    return true;
  } catch (error) {
    console.error('Error exporting accommodation data:', error);
    throw new Error('Failed to export accommodation data');
  }
};

// Helper function to convert shortened room type to full label
const getRoomTypeLabel = (type: 'd' | 't' | 'q'): string => {
  switch (type) {
    case 'd': return 'double';
    case 't': return 'triple';
    case 'q': return 'quadruple';
    default: return type;
  }
};

// New function to export all accommodation data across all hotels
export const exportAllAccommodationToExcel = async () => {
  try {
    console.log('Starting export for ALL accommodation tables');
    
    // Import necessary Firebase functions
    const { getDocs, collection, query, where } = await import('firebase/firestore');
    const { firestore } = await import('../lib/firebase/config');
    
    // Fetch all accommodation tables
    const tablesRef = collection(firestore, 'AccommodationTables');
    const tablesSnapshot = await getDocs(tablesRef);
    
    if (tablesSnapshot.empty) {
      throw new Error('No accommodation tables found');
    }

    console.log(`Found ${tablesSnapshot.size} accommodation tables`);
    
    // Fetch all pilgrims for package information
    const pilgrimsRef = collection(firestore, 'Pilgrims');
    const pilgrimsSnapshot = await getDocs(pilgrimsRef);
    
    // Create a map of pilgrim IDs to their package names, countries and passport numbers
    const pilgrimDetails = new Map();
    pilgrimsSnapshot.forEach(doc => {
      const data = doc.data();
      pilgrimDetails.set(doc.id, {
        package: data.package_nickName || '',
        country: data.Residence_Country || '',
        passport: data.Passport_Number || ''
      });
    });
    
    console.log(`Loaded ${pilgrimDetails.size} pilgrims' details information`);
    
    // Helper function to parse DD/MM/YYYY format to timestamp
    const parseDate = (dateStr: string): number => {
      if (!dateStr || typeof dateStr !== 'string' || !dateStr.includes('/')) {
        return 0;
      }
      try {
        const [day, month, year] = dateStr.split('/').map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year)) {
          return 0;
        }
        return new Date(year, month - 1, day).getTime();
      } catch (e) {
        console.error('Error parsing date:', dateStr, e);
        return 0;
      }
    };
    
    // Helper function to format date string
    const formatDateString = (dateInput: number | string): string => {
      // For string format in DD/MM/YYYY
      if (typeof dateInput === 'string' && dateInput.includes('/')) {
        // Extract day and month only
        const [day, month] = dateInput.split('/').map(num => parseInt(num, 10));
        // Return DD/M format
        return `${day}/${month}`;
      }
      
      // For timestamp
      try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) {
          console.warn('Invalid date input:', dateInput);
          return 'Invalid Date';
        }
        // Return in DD/M format without leading zeros for month
        return `${date.getDate()}/${date.getMonth() + 1}`;
      } catch (error) {
        console.error('Error formatting date:', error, dateInput);
        return 'Invalid Date';
      }
    };
    
    // Collect data from all tables
    const allRows: ExportRow[] = [];
    
    for (const tableDoc of tablesSnapshot.docs) {
      const table = tableDoc.data() as AccommodationTable;
      table.i = tableDoc.id; // Ensure table ID is set
      
      console.log(`Processing table: ${table.tn} (${table.hn})`);
      
      // Process each room in the table
      if (!table.r || !Array.isArray(table.r)) {
        console.warn('Table has no rooms or invalid rooms array:', table.tn);
        continue;
      }
      
      table.r.forEach((room: Room) => {
        if (!room.b || !Array.isArray(room.b)) {
          console.warn('Room has no beds or invalid beds array:', room.n);
          return;
        }

        room.b.forEach((bed: Bed, bedIndex: number) => {
          // Get all assignments for the bed
          if (bed.a && bed.a.length > 0) {
            // Helper function to check if two date ranges overlap
            const checkOverlap = (start1: number | string, end1: number | string, start2: number | string, end2: number | string) => {
              // Convert to timestamps for comparison
              const start1Timestamp = typeof start1 === 'string' ? parseDate(start1) : start1;
              const end1Timestamp = typeof end1 === 'string' ? parseDate(end1) : end1;
              const start2Timestamp = typeof start2 === 'string' ? parseDate(start2) : start2;
              const end2Timestamp = typeof end2 === 'string' ? parseDate(end2) : end2;
              
              return start1Timestamp < end2Timestamp && start2Timestamp < end1Timestamp;
            };

            // Group assignments by non-overlapping periods
            const periods: { assignments: any[]; startDate: number | string; endDate: number | string }[] = [];
            
            // Sort assignments by check-in date
            const sortedAssignments = [...bed.a].sort((a, b) => {
              const aTimestamp = typeof a.ci === 'string' ? parseDate(a.ci) : a.ci;
              const bTimestamp = typeof b.ci === 'string' ? parseDate(b.ci) : b.ci;
              return aTimestamp - bTimestamp;
            });
            
            sortedAssignments.forEach(assignment => {
              const assignmentStart = assignment.ci;
              const assignmentEnd = assignment.co;
              
              // Try to find an existing period that overlaps
              let foundPeriod = false;
              for (const period of periods) {
                if (checkOverlap(period.startDate, period.endDate, assignmentStart, assignmentEnd)) {
                  // Overlapping period found
                  period.assignments.push(assignment);
                  // We'll skip updating period bounds for simplicity in the all-accommodations export
                  foundPeriod = true;
                  break;
                }
              }
              
              // If no overlapping period found, create a new one
              if (!foundPeriod) {
                periods.push({
                  assignments: [assignment],
                  startDate: assignmentStart,
                  endDate: assignmentEnd
                });
              }
            });
            
            // Sort periods by start date
            periods.sort((a, b) => {
              const aTimestamp = typeof a.startDate === 'string' ? parseDate(a.startDate) : a.startDate;
              const bTimestamp = typeof b.startDate === 'string' ? parseDate(b.startDate) : b.startDate;
              return aTimestamp - bTimestamp;
            });
            
            // Process each period with its assignments
            periods.forEach((period, periodIndex) => {
              period.assignments.forEach(assignment => {
                try {
                  // Look up details for this pilgrim
                  const details = pilgrimDetails.get(assignment.pid) || { package: '', country: '', passport: '' };
                  
                  allRows.push({
                    hotel_name: table.hn || '',
                    package_name: details.package,
                    country: details.country,
                    passport_number: details.passport,
                    hotel_table_name: table.tn || '', // Will be excluded from export
                    room_number: String(room.n) || '',
                    room_type: getRoomTypeLabel(room.t),
                    bed_number: bedIndex + 1,
                    pilgrim_index: periodIndex + 1, // Using period index instead of assignment index
                    check_in: formatDateString(assignment.ci),
                    check_out: formatDateString(assignment.co),
                    pilgrim_name: assignment.pn || '',
                    pilgrim_gender: assignment.g === 'M' ? 'Male' : 'Female',
                    guide_name: assignment.gn || '',
                    organizer_name: assignment.on || '',
                    pilgrim_age: assignment.age || 0
                  });
                } catch (error) {
                  console.error('Error processing assignment:', error, {
                    assignment,
                    room: room.n,
                    bed: bedIndex
                  });
                }
              });
            });
          }
        });
      });
    }

    console.log(`Generated total of ${allRows.length} rows from all tables`);

    if (allRows.length === 0) {
      throw new Error('No accommodation data found across all hotels');
    }

    // Define the columns to include in the export (excluding hotel_table_name)
    const columns = [
      'hotel_name',
      'package_name',
      'country',
      'passport_number',
      'room_number',
      'room_type',
      'bed_number',
      'pilgrim_index',
      'check_in',
      'check_out',
      'pilgrim_name',
      'pilgrim_gender',
      'guide_name',
      'organizer_name',
      'pilgrim_age'
    ];

    // Create worksheet with only selected columns
    const ws = XLSX.utils.json_to_sheet(
      allRows.map(row => {
        const newRow: any = {};
        columns.forEach(col => {
          newRow[col] = row[col as keyof ExportRow];
        });
        return newRow;
      })
    );

    // Add English headers
    XLSX.utils.sheet_add_aoa(ws, [[
      'Hotel Name',
      'Package Name',
      'Country',
      'Passport Number',
      'Room Number',
      'Room Type',
      'Bed Number',
      'Period Index',
      'Check-in Date',
      'Check-out Date',
      'Pilgrim Name',
      'Gender',
      'Guide',
      'Organizer',
      'Age'
    ]], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // hotel_name
      { wch: 25 }, // package_name
      { wch: 20 }, // country
      { wch: 15 }, // passport_number
      { wch: 10 }, // room_number
      { wch: 15 }, // room_type
      { wch: 10 }, // bed_number
      { wch: 12 }, // pilgrim_index
      { wch: 15 }, // check_in
      { wch: 15 }, // check_out
      { wch: 30 }, // pilgrim_name
      { wch: 10 }, // pilgrim_gender
      { wch: 25 }, // guide_name
      { wch: 25 }, // organizer_name
      { wch: 10 }, // pilgrim_age
    ];

    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'All Accommodations');

    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const filename = `all-hotels-accommodation-${date}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);

    console.log('All accommodation export completed successfully');
    return true;
  } catch (error) {
    console.error('Error exporting all accommodation data:', error);
    throw new Error('Failed to export all accommodation data');
  }
};